import type { EntityScope } from './parseSlashQuery'
import { matchesQuery, normalizeSearchText } from './filterSearchItems'
import type { FlatPage } from './flattenNavigation'
import { getDocsHomeUrl } from '@/constants/docsUrl'

export type CommandAudience = 'admin' | 'owner' | 'employee' | 'client' | 'bank' | 'any'

export type SlashCommandKind = 'scope' | 'goto' | 'action'

export interface SlashCommandDef {
  id: string
  slashes: string[]
  title: string
  description: string
  keywords: string[]
  audience: CommandAudience[]
  kind: SlashCommandKind
  scope?: EntityScope
  path?: string
  href?: string
  action?: 'clock-in' | 'clock-out' | 'ask' | 'new-lead' | 'decode-vin' | 'price'
  hideWhenClockedIn?: boolean
  hideWhenClockedOut?: boolean
  hideOnMobile?: boolean
}

export interface CommandVisibilityContext {
  role?: string
  isOwner?: boolean
  isClientContext?: boolean
  isMobile?: boolean
  isTimerActive?: boolean
  assistantEnabled?: boolean
}

const STATIC_SLASH_COMMANDS: SlashCommandDef[] = [
  {
    id: 'scope-inventory',
    slashes: ['/inventory', '/truck', '/trucks'],
    title: 'Search inventory',
    description: 'Find trucks by VIN or stock number',
    keywords: ['vin', 'stock', 'truck', 'inventory'],
    audience: ['admin'],
    kind: 'scope',
    scope: 'inventory',
  },
  {
    id: 'scope-lead',
    slashes: ['/lead', '/leads'],
    title: 'Search leads',
    description: 'Find leads by number, customer, or company',
    keywords: ['lead', 'customer'],
    audience: ['admin'],
    kind: 'scope',
    scope: 'lead',
  },
  {
    id: 'scope-invoice',
    slashes: ['/invoice', '/invoices'],
    title: 'Search invoices',
    description: 'Find retail orders by invoice number or client',
    keywords: ['invoice', 'retail', 'order'],
    audience: ['admin'],
    kind: 'scope',
    scope: 'invoice',
  },
  {
    id: 'scope-client',
    slashes: ['/client', '/clients'],
    title: 'Search clients',
    description: 'Find portal clients by name, email, or company',
    keywords: ['client', 'customer', 'user'],
    audience: ['admin'],
    kind: 'scope',
    scope: 'client',
  },
  {
    id: 'scope-transport',
    slashes: ['/transport', '/transporter'],
    title: 'Search transport',
    description: 'Find transporter packages by VIN or stock number',
    keywords: ['transport', 'package', 'shipment'],
    audience: ['admin'],
    kind: 'scope',
    scope: 'transport',
  },
  {
    id: 'scope-goto',
    slashes: ['/goto', '/page'],
    title: 'Go to page',
    description: 'Jump to a portal page by name',
    keywords: ['goto', 'page', 'navigate'],
    audience: ['any'],
    kind: 'scope',
    scope: 'goto',
  },
  {
    id: 'goto-settings',
    slashes: ['/settings'],
    title: 'Settings',
    description: 'Open account settings',
    keywords: ['settings', 'account'],
    audience: ['any'],
    kind: 'goto',
    path: '/account-settings',
  },
  {
    id: 'goto-notifications',
    slashes: ['/notifications'],
    title: 'Notification Center',
    description: 'Open the notification center',
    keywords: ['notifications'],
    audience: ['admin'],
    kind: 'goto',
    path: '/notification-center',
  },
  {
    id: 'action-ask',
    slashes: ['/ask'],
    title: 'Ask assistant',
    description: 'Open the portal assistant',
    keywords: ['ask', 'assistant', 'chat', 'ai'],
    audience: ['admin'],
    kind: 'action',
    action: 'ask',
  },
  {
    id: 'action-new-lead',
    slashes: ['/new-lead'],
    title: 'New lead',
    description: 'Open lead management to create a lead',
    keywords: ['lead', 'create', 'new'],
    audience: ['admin'],
    kind: 'action',
    action: 'new-lead',
  },
  {
    id: 'action-decode-vin',
    slashes: ['/decode-vin'],
    title: 'Decode VIN',
    description: 'Open the VIN decoder',
    keywords: ['vin', 'decode'],
    audience: ['admin'],
    kind: 'action',
    action: 'decode-vin',
  },
  {
    id: 'action-price',
    slashes: ['/price'],
    title: 'Price truck',
    description: 'Open Gray Book calculator',
    keywords: ['price', 'graybook'],
    audience: ['admin'],
    kind: 'action',
    action: 'price',
  },
  {
    id: 'action-clock-in',
    slashes: ['/clock-in'],
    title: 'Clock in',
    description: 'Start the timesheet timer',
    keywords: ['clock', 'timer', 'timesheet'],
    audience: ['employee'],
    kind: 'action',
    action: 'clock-in',
    hideWhenClockedIn: true,
    hideOnMobile: true,
  },
  {
    id: 'action-clock-out',
    slashes: ['/clock-out'],
    title: 'Clock out',
    description: 'Stop the timesheet timer',
    keywords: ['clock', 'timer', 'timesheet'],
    audience: ['employee'],
    kind: 'action',
    action: 'clock-out',
    hideWhenClockedOut: true,
  },
]

/** Build goto commands from flattened navigation so slash list stays in sync with the sidebar. */
export function gotoCommandsFromPages(pages: FlatPage[]): SlashCommandDef[] {
  const docsUrl = getDocsHomeUrl()
  const seen = new Set<string>()
  const commands: SlashCommandDef[] = []

  for (const page of pages) {
    const slashes = page.slashes?.length ? page.slashes : [`/${page.segment.split('/').pop()}`]
    const primary = slashes[0]
    if (seen.has(primary)) continue
    seen.add(primary)

    const isOwnerOnly =
      page.path === '/optic-ltv-calculator' ||
      page.path === '/admin/performance-reviews' ||
      page.path === '/admin/client-activity'

    commands.push({
      id: `goto-${page.segment || page.id}`,
      slashes,
      title: page.title,
      description: page.description || `Open ${page.title}`,
      keywords: page.keywords || [page.title.toLowerCase()],
      audience: isOwnerOnly ? ['owner'] : page.href ? ['admin'] : ['admin'],
      kind: 'goto',
      path: page.path,
      href: page.href || (page.title === 'Docs' ? docsUrl : undefined),
    })
  }

  return commands
}

export function commandVisible(
  command: SlashCommandDef,
  ctx: CommandVisibilityContext,
): boolean {
  if (command.hideOnMobile && ctx.isMobile) return false
  if (command.hideWhenClockedIn && ctx.isTimerActive) return false
  if (command.hideWhenClockedOut && !ctx.isTimerActive) return false
  if (command.action === 'ask') {
    // Chatbot is admin-only (not babyAdmin), plus feature flag.
    return ctx.assistantEnabled === true && ctx.role === 'admin'
  }

  return command.audience.some(audience => {
    if (audience === 'any') return true
    if (audience === 'owner') return ctx.isOwner === true
    if (audience === 'admin') return ctx.role === 'admin' || ctx.role === 'babyAdmin'
    if (audience === 'employee') {
      return (
        ctx.isOwner !== true &&
        ctx.isClientContext !== true &&
        ctx.role !== 'client' &&
        ctx.role !== 'bankAgent'
      )
    }
    if (audience === 'client') return ctx.isClientContext === true || ctx.role === 'client'
    if (audience === 'bank') return ctx.role === 'bankAgent'
    return false
  })
}

export function buildSlashCommands(pages: FlatPage[]): SlashCommandDef[] {
  return [...STATIC_SLASH_COMMANDS, ...gotoCommandsFromPages(pages)]
}

export function visibleSlashCommands(
  ctx: CommandVisibilityContext,
  pages: FlatPage[] = [],
): SlashCommandDef[] {
  return buildSlashCommands(pages).filter(command => commandVisible(command, ctx))
}

export function filterSlashCommands(
  commands: SlashCommandDef[],
  tokenOrQuery: string,
): SlashCommandDef[] {
  const term = normalizeSearchText(tokenOrQuery)
  if (!term) return commands
  const slashTerm = term.startsWith('/') ? term : `/${term}`
  return commands.filter(command => {
    if (command.slashes.some(slash => slash.startsWith(slashTerm) || slash.includes(term))) {
      return true
    }
    return matchesQuery([command.title, command.description, ...command.keywords], term)
  })
}

/** @deprecated Prefer buildSlashCommands(pages). Kept for any leftover imports. */
export const SLASH_COMMANDS = STATIC_SLASH_COMMANDS
